import { useCallback, useEffect, useState } from "react";

import usePDFDownload from "@Components/shared/hooks/usePDFDownload";
import useStyledPDF from "@Components/shared/hooks/useStyledPDF";
import SessionManager from "@Core/session";
import ComplianceService from "@Services/ComplianceService";
import { notification } from "antd";
import { IComplianceStatus } from "@Models/IComplianceDetails";

type IReportAction = "download" | "preview";

/**
 * Custom hook for managing compliance report functionality.
 * Provides methods for requesting, checking status, loading, previewing, and downloading compliance reports.
 */
const useComplianceReport = () => {
    // ** State Hooks ** //
    const [requestStatusLoading, setRequestStatusLoading] = useState<boolean>(false);
    const [requestStatus, setRequestStatus] = useState<IComplianceStatus | "">("");
    const [requestReportLoading, setRequestReportLoading] = useState<boolean>(false);
    const [reportAction, setReportAction] = useState<IReportAction>();
    const [Report, setReport] = useState<() => JSX.Element | null>();

    // ** Hooks ** //
    const download = usePDFDownload();
    const createStyledPDFReport = useStyledPDF({ theme: "default" });

    // ** Variables ** //
    const complianceService = new ComplianceService();
    const { fullName, userId } = SessionManager.getServiceUser();

    // ** Report ** //

    /**
     * Creates a new compliance report request.
     * Sets the request status to "pending" if successful, or displays an error notification if there are errors.
     */
    const requestReport = async () => {
        setRequestStatus("");
        setRequestReportLoading(true);
        const result = await complianceService.create(userId, "access");
        setRequestReportLoading(false);
        if (result.hasErrors) {
            notification.error({
                message: "There was an error creating your request. You may already have a request pending.",
            });
            return;
        } else {
            notification.success({
                message: "Thanks, your data access request has been accepted.",
            });
            setRequestStatus("pending");
        }
    };

    /**
     * Retrieves the status of the compliance report request.
     */
    const getRequestStatus = useCallback(async () => {
        setRequestStatusLoading(true);

        const result = await complianceService.getRequestStatus(userId);
        setRequestStatusLoading(false);

        if (!result.hasErrors) {
            setRequestStatus(result.value);
        }
    }, [userId]);

    /**
     * Loads the compliance report and saves it to the component state.
     * If the report is already loaded, it returns the existing report.
     * If there are errors or no report data, it displays an error notification.
     * @param action The action to perform with the loaded report ("download" or "preview").
     */
    const loadReport = async (action: "download" | "preview"): Promise<typeof Report> => {
        if (!!Report?.()) {
            // don't load the report if already loaded
            return Promise.resolve(Report);
        }
        setReportAction(action);

        const result = await complianceService.getReport(userId);

        if (result.hasErrors || !result.value) {
            notification.error({ message: `Unable to ${action} the report.` });
            return Promise.reject(undefined);
        }

        const reportContent = !result.hasErrors ? result.value.content : [];

        const report = () => () => !!reportContent ? createStyledPDFReport({ content: reportContent }) : null;

        setReport(report);
        setReportAction(undefined);
        return Promise.resolve(report());
    };

    /**
     * Previews the compliance report.
     */
    const previewReport = async () => {
        return await loadReport("preview");
    };

    /**
     * Downloads the compliance report as a PDF file.
     * If the report is not loaded, it loads the report first.
     * Displays a success notification with the downloaded file name.
     */
    const downloadReport = async () => {
        const file = await loadReport("download");
        if (!file) return;

        const fileName = "Totum User Data - " + fullName + ".pdf";
        await download({ file, fileName }).then(
            (success) =>
                success &&
                notification.success({
                    message: `Downloaded: ${fileName}.`,
                })
        );
    };

    // Only check for the report status on page load.
    useEffect(() => {
        getRequestStatus();
    }, [getRequestStatus, userId]);

    return {
        Report,
        downloadReport,
        previewReport,
        reportAction,
        requestReport,
        requestReportLoading,
        requestStatus,
        requestStatusLoading,
    };
};

export default useComplianceReport;
